import React from "react";
import { motion } from "framer-motion";
import { Box, Accordion, AccordionSummary, AccordionDetails, Typography } from "@mui/material";
import ExpandMoreRoundedIcon from "@mui/icons-material/ExpandMoreRounded";
import ThermostatRoundedIcon from "@mui/icons-material/ThermostatRounded";
import MonitorHeartRoundedIcon from "@mui/icons-material/MonitorHeartRounded";
import PsychologyRoundedIcon from "@mui/icons-material/PsychologyRounded";
import AccessibilityNewRoundedIcon from "@mui/icons-material/AccessibilityNewRounded";
import ShieldRoundedIcon from "@mui/icons-material/ShieldRounded";
import ChildCareRoundedIcon from "@mui/icons-material/ChildCareRounded";
import LaptopMacRoundedIcon from "@mui/icons-material/LaptopMacRounded";
import MedicationRoundedIcon from "@mui/icons-material/MedicationRounded";
import SEO from "../components/seo/SEO";

const services = [
  { icon: ThermostatRoundedIcon, title: "Common Illnesses", items: ["Fever & Flu", "Allergies", "Infections", "Digestive Issues"] },
  { icon: MonitorHeartRoundedIcon, title: "Chronic Disease Management", items: ["Diabetes Care", "Hypertension", "Heart Health", "Thyroid"] },
  { icon: PsychologyRoundedIcon, title: "Mental Health", items: ["Therapy", "Anxiety", "Depression", "Stress Management"] },
  { icon: AccessibilityNewRoundedIcon, title: "Pain Management", items: ["Back Pain", "Joint Pain", "Physiotherapy", "Sports Injuries"] },
  { icon: ShieldRoundedIcon, title: "Preventive Care", items: ["Health Checkups", "Vaccinations", "Cancer Screening", "Wellness Programs"] },
  { icon: ChildCareRoundedIcon, title: "Pediatric Care", items: ["Child Wellness", "Vaccinations", "Growth Monitoring", "Nutrition"] },
  { icon: LaptopMacRoundedIcon, title: "Telehealth", items: ["Video Consultations", "Chat Support", "E-Prescriptions", "Remote Monitoring"] },
  { icon: MedicationRoundedIcon, title: "Pharmacy Services", items: ["Prescription Delivery", "Medication Management", "Discounted Labs"] }
];

export default function Services() {
  return (
    <>
      <SEO
        title="Healthcare Services"
        description="Comprehensive healthcare services including concierge care, mental health, chronic disease management, and 24/7 telehealth."
        canonical="/services"
      />
      <Box component="main" sx={{ pt: "96px" }}>
        <Box component="section" className="section-padding wellness-bg">
          <Box className="container-custom">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              style={{ textAlign: "center", maxWidth: 768, margin: "0 auto 64px" }}
            >
              <Typography
                component="h1"
                sx={{
                  fontSize: { xs: "2.25rem", md: "3rem" },
                  fontWeight: 800,
                  mb: 3,
                  lineHeight: 1.1
                }}
              >
                Our Healthcare
                <Box component="span" className="gradient-text" sx={{ display: "block" }}>
                  Services
                </Box>
              </Typography>
              <Typography sx={{ fontSize: "1.125rem", color: "text.secondary" }}>
                Comprehensive care for every stage of life, from preventive wellness to specialized
                treatments.
              </Typography>
            </motion.div>

            <Box sx={{ maxWidth: 896, mx: "auto", display: "grid", gap: 2 }}>
              {services.map((s, i) => {
                const Icon = s.icon;
                return (
                  <Accordion key={s.title} disableGutters sx={{ borderRadius: "16px", overflow: "hidden" }} className="glass-card">
                    <AccordionSummary expandIcon={<ExpandMoreRoundedIcon />} sx={{ py: 1.5, px: 3 }}>
                      <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                        <Icon sx={{ color: "primary.main" }} />
                        <Typography sx={{ fontWeight: 700, fontSize: "1.125rem" }}>{s.title}</Typography>
                      </Box>
                    </AccordionSummary>
                    <AccordionDetails sx={{ px: 3, pb: 3 }}>
                      <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" }, gap: 1.5, pl: { xs: 0, sm: 4 } }}>
                        {s.items.map((item) => (
                          <Box key={item} sx={{ display: "flex", alignItems: "center", gap: 1.5, color: "text.secondary" }}>
                            <Box sx={{ width: 6, height: 6, borderRadius: "50%", bgcolor: "primary.main" }} />
                            {item}
                          </Box>
                        ))}
                      </Box>
                    </AccordionDetails>
                  </Accordion>
                );
              })}
            </Box>
          </Box>
        </Box>
      </Box>
    </>
  );
}
